/**
 * Master graph for Yearly Demand: one line per product line across the fiscal years, each in its own
 * colour. Full years are solid; this year runs dashed to its projection (when there's a valid one).
 * The legend under it is a table: click a row to show or hide that line. Scale: Units, or Growth
 * (each line indexed to its first full year = 100) so small and big lines can be compared.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { DemandTableRow } from "@march/shared";
import { fmtNum } from "../lib/csv.ts";
import { change, totalsOf, type Totals } from "./GroupedDemand.tsx";

const COLORS = Array.from({ length: 10 }, (_, i) => `var(--s${i + 1})`);
const short = (fy: string) => fy.replace(/^FY20/, "FY");

type Series = { key: string; label: string; t: Totals; color: string; skus: number };
type Scale = "units" | "growth";

export function DemandGraph({
  rows, fys, currentFy, groupOf, unit,
}: {
  rows: DemandTableRow[];
  fys: string[];
  currentFy: string;
  groupOf: (itemid: string) => { key: string; label: string };
  unit: string;
}) {
  const fyKey = fys.join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fullFys = useMemo(() => fys.filter((f) => f !== currentFy).slice(-3), [fyKey, currentFy]);
  const xs = useMemo(() => [...fullFys, currentFy], [fullFys, currentFy]);

  const series: Series[] = useMemo(() => {
    const m = new Map<string, { label: string; rows: DemandTableRow[] }>();
    for (const r of rows) {
      const g = groupOf(r.itemid);
      if (!m.has(g.key)) m.set(g.key, { label: g.label, rows: [] });
      m.get(g.key)!.rows.push(r);
    }
    // Biggest last full year first, so the colours and the default "top 5" go to the lines that matter.
    const last = fullFys[fullFys.length - 1];
    return [...m.entries()]
      .map(([key, g]) => ({ key, label: g.label, t: totalsOf(g.rows, fys, currentFy), skus: g.rows.length }))
      .sort((a, b) => (b.t.used[last] ?? 0) - (a.t.used[last] ?? 0))
      .map((s, i) => ({ ...s, color: COLORS[i % COLORS.length] }));
  }, [rows, fys, currentFy, groupOf, fullFys]);

  const [on, setOn] = useState<Set<string>>(new Set());
  const seriesKey = series.map((s) => s.key).join("|");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setOn(new Set(series.slice(0, 5).map((s) => s.key))), [seriesKey]);
  const [scale, setScale] = useState<Scale>("units");
  const [hover, setHover] = useState<number | null>(null);

  // Responsive width.
  const box = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(900);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth - 36));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const base = fullFys[0];
  const val = (s: Series, i: number): number | null => {
    const fy = xs[i];
    const cur = fy === currentFy;
    const v = cur ? s.t.proj : s.t.used[fy] ?? 0;
    if (v == null) return null;
    if (scale === "units") return v;
    const b = s.t.used[base] ?? 0;
    return b > 0 ? (v / b) * 100 : null;
  };
  const visible = series.filter((s) => on.has(s.key));
  const allVals = visible.flatMap((s) => xs.map((_, i) => val(s, i))).filter((v): v is number => v != null);
  const yMax = niceMax(Math.max(...allVals, scale === "growth" ? 120 : 10));
  const yMin = scale === "growth" ? Math.min(0, ...allVals) : 0;

  const H = 480, padL = 56, padR = 16, padT = 12, padB = 30;
  const plotW = Math.max(300, w) - padL - padR;
  const X = (i: number) => padL + (xs.length === 1 ? plotW / 2 : (i / (xs.length - 1)) * plotW);
  // Units: when big lines dwarf the rest, 0–10k gets 60% of the height and the scale steps up above
  // 10k (marked on the chart), so the small lines aren't flattened against the floor.
  const BREAK = 10000;
  const split = scale === "units" && yMax > BREAK * 1.5;
  const plotH = H - padT - padB;
  const Y = (v: number) => {
    if (!split) return padT + (1 - (v - yMin) / (yMax - yMin || 1)) * plotH;
    const low = 0.6 * plotH;
    if (v <= BREAK) return padT + plotH - (v / BREAK) * low;
    return padT + plotH - low - ((v - BREAK) / (yMax - BREAK)) * (plotH - low);
  };
  const ticks = split
    ? [0, 2500, 5000, 7500, BREAK, ...Array.from({ length: 3 }, (_, i) => BREAK + ((yMax - BREAK) * (i + 1)) / 3)]
    : Array.from({ length: 5 }, (_, i) => yMin + ((yMax - yMin) * i) / 4);
  const fmtY = (v: number) => (scale === "growth" ? `${Math.round(v)}` : v >= 1000 ? `${Math.round(v / 100) / 10}k` : fmtNum(v));

  const toggle = (k: string) => setOn((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 8 }}>
        <span className="seg" role="group" aria-label="Scale">
          <button type="button" aria-pressed={scale === "units"} onClick={() => setScale("units")}>Units</button>
          <button type="button" aria-pressed={scale === "growth"} onClick={() => setScale("growth")}>Growth ({short(base)} = 100)</button>
        </span>
        <span className="spacer" />
        <button type="button" className="btn" onClick={() => setOn(new Set(series.slice(0, 5).map((s) => s.key)))}>Top 5</button>
        <button type="button" className="btn" onClick={() => setOn(new Set(series.map((s) => s.key)))}>All</button>
        <button type="button" className="btn" onClick={() => setOn(new Set())}>None</button>
      </div>

      <div className="mg-layout">
      <div className="mg" ref={box} onMouseLeave={() => setHover(null)}>
        <svg height={H} viewBox={`0 0 ${Math.max(300, w)} ${H}`} role="img" aria-label="Units used per fiscal year by product line">
          <g className="grid">
            {ticks.map((t) => <line key={t} x1={padL} x2={padL + plotW} y1={Y(t)} y2={Y(t)} />)}
          </g>
          {split && (
            <g>
              <line x1={padL} x2={padL + plotW} y1={Y(BREAK)} y2={Y(BREAK)} stroke="var(--text-muted)" strokeDasharray="2 4" />
              <text x={padL + plotW} y={Y(BREAK) - 6} textAnchor="end" style={{ fontSize: 11, fill: "var(--text-muted)" }}>scale changes above 10k</text>
            </g>
          )}
          {scale === "growth" && <line x1={padL} x2={padL + plotW} y1={Y(100)} y2={Y(100)} stroke="var(--text-faint)" strokeDasharray="4 4" />}
          {hover != null && <rect className="hoverband" x={X(hover) - 28} y={padT} width={56} height={H - padT - padB} rx={6} />}
          <g className="axis">
            {ticks.map((t) => <text key={t} x={padL - 8} y={Y(t) + 4} textAnchor="end">{fmtY(t)}</text>)}
            {xs.map((fy, i) => (
              <text key={fy} x={X(i)} y={H - 8} textAnchor="middle">{fy === currentFy ? `${short(fy)} proj.` : short(fy)}</text>
            ))}
          </g>
          {visible.map((s) => {
            const pts = xs.map((_, i) => val(s, i));
            const solid = pts.slice(0, fullFys.length).map((v, i) => (v == null ? null : `${X(i)},${Y(v)}`)).filter(Boolean).join(" ");
            const lastFull = pts[fullFys.length - 1];
            const proj = pts[xs.length - 1];
            return (
              <g key={s.key} style={{ color: s.color }}>
                <polyline points={solid} fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinejoin="round" />
                {lastFull != null && proj != null && (
                  <line x1={X(fullFys.length - 1)} y1={Y(lastFull)} x2={X(xs.length - 1)} y2={Y(proj)} stroke="currentColor" strokeWidth={2.5} strokeDasharray="6 5" />
                )}
                {pts.map((v, i) => v == null ? null : (
                  <circle key={i} cx={X(i)} cy={Y(v)} r={i === xs.length - 1 ? 4 : 3.5} fill={i === xs.length - 1 ? "var(--surface)" : "currentColor"} stroke="currentColor" strokeWidth={2} />
                ))}
              </g>
            );
          })}
          {/* hover targets, one per year */}
          {xs.map((_, i) => (
            <rect key={i} x={X(i) - plotW / (2 * Math.max(1, xs.length - 1))} y={padT} width={plotW / Math.max(1, xs.length - 1)} height={H - padT - padB} fill="transparent" onMouseEnter={() => setHover(i)} />
          ))}
        </svg>
        {hover != null && visible.length > 0 && (
          <div className="mg-tip" style={{ left: Math.min(X(hover) + 24, Math.max(300, w) - 200), top: 20 }}>
            <h5>{xs[hover] === currentFy ? `${xs[hover]} projected` : xs[hover]}</h5>
            {[...visible]
              .sort((a, b) => (val(b, hover) ?? -1) - (val(a, hover) ?? -1))
              .map((s) => {
                const v = val(s, hover);
                return (
                  <div key={s.key}>
                    <i style={{ background: s.color }} />
                    {s.label}
                    <b>{v == null ? "—" : scale === "growth" ? Math.round(v) : `${fmtNum(Math.round(v))} ${unit}`}</b>
                  </div>
                );
              })}
          </div>
        )}
        <div className="sc-key" style={{ marginTop: 4 }}>
          <span><i className="k line" />full years</span>
          <span><i className="k dash" />{short(currentFy)} projected</span>
          <span>Click a row in the table to show or hide its line.</span>
        </div>
        {visible.length === 0 && <div className="u-empty" style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>Pick lines below to draw them.</div>}
      </div>

      <div className="tablewrap mg-legend">
        <table className="dt legend">
          <thead>
            <tr>
              <th>Product line <span className="sub" style={{ fontWeight: 400 }}>· SKUs</span></th>
              {fullFys.map((fy) => <th key={fy}>{short(fy)}</th>)}
              <th>{short(currentFy)} proj.</th>
              <th title={fullFys.length >= 2 ? `${fullFys[fullFys.length - 1]} vs ${fullFys[fullFys.length - 2]}` : ""}>Last year</th>
            </tr>
          </thead>
          <tbody>
            {series.map((s) => {
              const chg = change(s.t, fys, currentFy);
              const isOn = on.has(s.key);
              return (
                <tr key={s.key} className={isOn ? "" : "off"} onClick={() => toggle(s.key)} aria-pressed={isOn} role="button" tabIndex={0}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), toggle(s.key))}>
                  <td className="l"><span className="lg-chip" style={{ background: s.color }} />{s.label} <span className="sub" title={`${fmtNum(Math.round(s.t.used[currentFy] ?? 0))} so far this year`}>· {s.skus}</span></td>
                  {fullFys.map((fy) => <td key={fy}>{fmtNum(Math.round(s.t.used[fy] ?? 0))}</td>)}
                  <td>{s.t.proj != null ? fmtNum(Math.round(s.t.proj)) : <span className="muted">—</span>}</td>
                  <td>{chg ? <span className={`ychg ${chg.cls}`}>{chg.text}</span> : <span className="muted">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
    </>
  );
}

function niceMax(v: number) {
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  for (const m of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) if (m * p >= v) return m * p;
  return 10 * p;
}
